import React, { useState, useEffect } from 'react';
import { Users, UserPlus, 
  Award, Shield, CheckCircle, Star, Building, 
  Phone, Mail, MapPin, Send, ArrowRight, Home,
  ExternalLink, FileText, Scale,  
  Globe, Lock,  Target, Zap
} from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const OfficialAccreditations = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    intro: false,
    accreditations: false,
    benefits: false,
    contact: false
  });

  useEffect(() => {
    setIsVisible(true);
    
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          if (sectionId) {
            setVisibleSections(prev => ({
              ...prev,
              [sectionId]: true
            }));
          }
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all sections
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const whatsappUrl = "https://wa.me/966115115945";

  const accreditations = [
    {
      id: 1,
      title: "اعتماد الغرفة التجارية",
      subtitle: "Commercial Chamber Accreditation",
      description: "نحن معتمدون رسمياً لدى الغرفة التجارية في المملكة العربية السعودية، مما يضمن جودة خدماتنا القانونية وموثوقيتها.",
      icon: Building,
      image: "/images/Home/تصميم بدون عنوان - 2025-08-10T201430.005.png",
      features: [
        "اعتماد رسمي من الغرفة التجارية",
        "التزام بمعايير الجودة المهنية",
        "معتمد لدى الهيئة السعودية للمحامين",
        "خدمات قانونية معتمدة"
      ],
      badge: "معتمد رسمياً"
    },
    {
      id: 2,
      title: "الصفحات الزرقاء",
      subtitle: "Blue Pages Directory",
      description: "صفحتنا في دليل الصفحات الزرقاء الرسمي تؤكد مكانتنا كشركة محاماة واستشارات قانونية معتمدة وموثوقة.",
      icon: Globe,
      image: "/images/Home/تصميم بدون عنوان - 2025-08-10T201759.709.png",
      features: [
        "صفحة رسمية في الصفحات الزرقاء",
        "رقم تسجيل: 854415",
        "معلومات موثقة ومعتمدة",
        "مرجعية رسمية للعملاء"
      ],
      badge: "مسجل رسمياً",
      externalLink: "https://bluepages.com.sa/companies/854415"
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "الثقة والموثوقية",
      description: "الاعتمادات الرسمية تضمن ثقة العملاء في جودة خدماتنا القانونية"
    },
    {
      icon: Award,
      title: "الجودة المهنية",
      description: "نلتزم بأعلى معايير الجودة المهنية والقانونية المعتمدة"
    },
    {
      icon: CheckCircle,
      title: "الشفافية",
      description: "جميع معلوماتنا وخدماتنا موثقة ومعتمدة رسمياً"
    },
    {
      icon: UserPlus,
      title: "الخبرة المعترف بها",
      description: "خبرتنا القانونية معترف بها رسمياً من الجهات المختصة"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100" dir="rtl">
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <div 
        data-section="hero"
        className={`relative py-12 sm:py-24 overflow-hidden transition-all duration-1000 mt-16 sm:mt-20 ${visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ backgroundColor: '#0a3a23' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='5'/%3E%3Ccircle cx='53' cy='7' r='5'/%3E%3Ccircle cx='30' cy='30' r='5'/%3E%3Ccircle cx='7' cy='53' r='5'/%3E%3Ccircle cx='53' cy='53' r='5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        
        <div className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center text-white transition-all duration-1000 delay-300 ${visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="mb-4 sm:mb-8">
            <span className="text-white text-lg sm:text-xl animate-pulse">شركة أنس عبد الرحمن خياط</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
            <span className="text-white">الاعتمادات الرسمية</span>
            <div className="w-20 sm:w-32 h-1 bg-white mx-auto mt-2 sm:mt-4 animate-pulse"></div>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl opacity-90 max-w-2xl sm:max-w-4xl mx-auto leading-relaxed mb-4 sm:mb-8 px-4">
            نفتخر باعتماداتنا الرسمية التي تؤكد جودة خدماتنا القانونية وموثوقيتها
          </p>
          <p className="text-sm sm:text-base md:text-lg opacity-80 max-w-xl sm:max-w-3xl mx-auto px-4">
            اعتمادات معتمدة من الجهات الرسمية في المملكة العربية السعودية
          </p>
        </div>

        {/* Navigation Breadcrumb */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-1 sm:gap-2 text-white text-xs sm:text-sm">
          <Link to="/" className="flex items-center gap-1 hover:text-gray-200 transition-colors">
            <Home className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>الرئيسية</span>
          </Link>
          <span>/</span>
          <div className="flex items-center gap-1">
            <Award className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>الاعتمادات الرسمية</span>
          </div>
        </div>
      </div>

      {/* Introduction Section */}
      <div data-section="intro" className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className={`transition-all duration-1000 ${visibleSections.intro ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8">
                لماذا الاعتمادات الرسمية مهمة؟
              </h2>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                <p>
                  الاعتمادات الرسمية هي شهادة ثقة من الجهات المختصة تؤكد جودة خدماتنا القانونية والتزامنا بأعلى معايير المهنة.
                </p>
                <p>
                  نحن نفتخر باعتمادنا الرسمي لدى الغرفة التجارية وصفحتنا في الصفحات الزرقاء، مما يضمن لعملائنا الثقة والموثوقية.
                </p>
                <div className="p-4 sm:p-6 rounded-xl border-r-4" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)', borderColor: '#0a3a23' }}>
                  <strong style={{ color: '#0a3a23' }}>اعتماداتنا الرسمية هي ضمانة لجودة خدماتنا والتزامنا بأعلى معايير المهنة القانونية.</strong>
                </div>
              </div>
            </div>
            
            <div className={`relative transition-all duration-1000 delay-300 ${visibleSections.intro ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <img 
                src="/images/services/service31.png"
                alt="الاعتمادات الرسمية"
                className="rounded-xl sm:rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Accreditations Section */}
      <div data-section="accreditations" className="py-12 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.accreditations ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              اعتماداتنا الرسمية
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
              نفتخر باعتماداتنا الرسمية التي تؤكد جودة خدماتنا القانونية
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {accreditations.map((accreditation, index) => (
              <div 
                key={accreditation.id} 
                className={`bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden group transform hover:scale-105 ${visibleSections.accreditations ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img 
                    src={accreditation.image} 
                    alt={accreditation.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 sm:top-6 right-4 sm:right-6">
                    <div className="bg-white text-[#0a3a23] px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                      {accreditation.badge}
                    </div>
                  </div>
                  <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6">
                    <accreditation.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                </div>
                
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 group-hover:text-[#0a3a23] transition-colors">
                    {accreditation.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 font-medium">
                    {accreditation.subtitle}
                  </p>
                  <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                    {accreditation.description}
                  </p>
                  
                  <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                    {accreditation.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0a3a23] flex-shrink-0" />
                        <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {accreditation.externalLink && (
                    <a
                      href={accreditation.externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 sm:gap-3 text-[#0a3a23] hover:text-[#0a3a23]/80 transition-colors font-medium"
                    >
                      <span>عرض الصفحة الرسمية</span>
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div data-section="benefits" className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.benefits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              مميزات الاعتمادات الرسمية
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
              الاعتمادات الرسمية تضمن لكم الثقة والموثوقية في جميع خدماتنا
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center transition-all duration-500 transform hover:scale-105 ${visibleSections.benefits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)' }}>
                  <benefit.icon className="w-8 h-8 sm:w-10 sm:h-10 text-[#0a3a23]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {benefit.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
            نفتخر باعتماداتنا الرسمية
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
            تواصل معنا للاستفادة من خدماتنا القانونية المعتمدة
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href={`${whatsappUrl}?text=أريد استشارة قانونية من شركة معتمدة رسمياً`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 sm:gap-3 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
              style={{ backgroundColor: '#0a3a23' }}
            >
              <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
              تواصل عبر الواتساب
            </a>
            <Link
              to="/Contact"
              className="inline-flex items-center gap-2 sm:gap-3 bg-gray-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-gray-900 transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
            >
              <Send className="w-5 h-5 sm:w-6 sm:h-6" />
              تواصل معنا
            </Link>
          </div>
        </div>
      </div>

      {/* Floating Contact Buttons */}
      <a
        href="tel:920033053"
        className="fixed bottom-20 sm:bottom-28 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href={`${whatsappUrl}?text=أريد استشارة قانونية من شركة معتمدة رسمياً`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 sm:bottom-6 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default OfficialAccreditations;


